import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import TablePagination from '../../components/TablePagination';
import StatusBadge from '../../components/StatusBadge';
import { printTableElement } from '../../utils/exportUtils';
import { deviationsAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';

const DEVIATION_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under-review',
  Approved: 'approved',
  Rejected: 'rejected',
};

const formatCurrency = (val) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val)}`
);

const Deviations = () => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [deviations, setDeviations] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [newDeviation, setNewDeviation] = useState({
    project_id: '',
    category: 'Deviation',
    title: '',
    description: '',
    boq_item: '',
    original_qty: '',
    revised_qty: '',
    original_rate: '',
    revised_rate: '',
    remarks: '',
    attachments: [],
  });
  const tableRef = useRef(null);

  const perPage = 50;
  useEffect(() => {
    const loadDeviations = async () => {
      try {
        const params = { page, pageSize: perPage };
        if (projectFilter !== 'all') params.projectId = projectFilter;
        const response = await deviationsAPI.getAll(params);
        const { rows: rawRows, totalCount: cnt } = unwrapListResponse(response);
        const rows = safeArray(rawRows).map((row) => {
          const payload = row.data || {};
          const originalAmount = payload.original_amount ?? row.original_amount ?? 0;
          const revisedAmount = payload.revised_amount ?? row.revised_amount ?? 0;
          const varianceAmount = payload.variance_amount ?? row.variance_amount ?? Math.max(0, revisedAmount - originalAmount);
          return {
            id: row.id,
            project_id: row.projectId ?? row.project_id ?? 1,
            deviation_no: payload.deviation_no ?? row.deviation_no ?? `DEV-${row.id}`,
            title: row.title || payload.title || '-',
            category: row.category || payload.category || 'Deviation',
            description: payload.description ?? row.description ?? '-',
            boq_item: payload.boq_item ?? row.boq_item ?? '-',
            original_qty: payload.original_qty ?? row.original_qty ?? 0,
            revised_qty: payload.revised_qty ?? row.revised_qty ?? 0,
            unit: payload.unit ?? row.unit ?? 'Nos',
            original_rate: payload.original_rate ?? row.original_rate ?? 0,
            revised_rate: payload.revised_rate ?? row.revised_rate ?? 0,
            original_amount: originalAmount,
            revised_amount: revisedAmount,
            variance_amount: varianceAmount,
            variance_pct: payload.variance_pct ?? row.variance_pct ?? (originalAmount > 0 ? Number(((varianceAmount / originalAmount) * 100).toFixed(1)) : 0),
            raised_by: payload.raised_by ?? row.raised_by ?? '-',
            raised_date: payload.raised_date ?? row.raised_date ?? '',
            status: row.status || payload.status || 'Submitted',
            approved_by: payload.approved_by ?? row.approved_by ?? null,
            approved_date: payload.approved_date ?? row.approved_date ?? null,
            linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
            remarks: payload.remarks ?? row.remarks ?? '',
            attachments: payload.attachments ?? row.attachments ?? [],
          };
        });
        setDeviations(rows);
        setTotalCount(cnt);
      } catch (error) {
        console.warn('Deviations API unavailable, using local data:', error?.message || error);
        setDeviations([]);
        setTotalCount(0);
      } finally {
        setDataReady(true);
      }
    };
    loadDeviations();
  }, [page, projectFilter]);

  const projects = safeArray(projectOptions);

  const filteredItems = useMemo(() => {
    let items = [...safeArray(deviations)];
    if (categoryFilter !== 'all') items = items.filter(i => i.category === categoryFilter);
    if (statusFilter !== 'all') items = items.filter(i => i.status === statusFilter);
    return items;
  }, [deviations, categoryFilter, statusFilter]);

  const totalVariance = filteredItems.reduce((s, i) => s + (i?.variance_amount ?? 0), 0);
  const showProjectColumn = projectFilter === 'all';
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));

  const getStatusClass = (status) => DEVIATION_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Deviations & Extra Items</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }
  const handleSaveDeviation = async () => {
    if (!newDeviation.project_id || !newDeviation.title.trim()) {
      notifyError('Project and title are required.');
      return;
    }
    if (editingId) {
      if (!(await confirmDialog('Save changes to this deviation?', 'Edit Deviation'))) return;
    } else {
      if (!(await confirmDialog('Create this deviation?', 'New Deviation'))) return;
    }
    const originalQty = Number(newDeviation.original_qty || 0);
    const revisedQty = Number(newDeviation.revised_qty || 0);
    const originalRate = Number(newDeviation.original_rate || 0);
    const revisedRate = Number(newDeviation.revised_rate || 0);
    const originalAmount = originalQty * originalRate;
    const revisedAmount = revisedQty * revisedRate;
    const varianceAmount = Math.max(0, revisedAmount - originalAmount);
    const variancePct = originalAmount > 0 ? Number(((varianceAmount / originalAmount) * 100).toFixed(1)) : 0;

    try {
      const baseData = {
        projectId: Number(newDeviation.project_id),
        title: newDeviation.title.trim(),
        category: newDeviation.category,
        status: 'Submitted',
        data: {
          description: newDeviation.description.trim(),
          boq_item: newDeviation.boq_item.trim(),
          original_qty: originalQty,
          revised_qty: revisedQty,
          unit: 'Nos',
          original_rate: originalRate,
          revised_rate: revisedRate,
          original_amount: originalAmount,
          revised_amount: revisedAmount,
          variance_amount: varianceAmount,
          variance_pct: variancePct,
          raised_by: JSON.parse(localStorage.getItem('user') || '{}')?.name || 'User',
          raised_date: new Date().toISOString().slice(0, 10),
          remarks: newDeviation.remarks.trim(),
          attachments: newDeviation.attachments || [],
        },
      };

      const response = editingId
        ? await deviationsAPI.update(editingId, baseData)
        : await deviationsAPI.create(baseData);

      const row = response?.data;
      const mapped = {
        id: row.id,
        project_id: row.projectId ?? Number(newDeviation.project_id),
        deviation_no: row.deviation_no || `DEV-${row.id}`,
        title: row.title || newDeviation.title,
        category: row.category || newDeviation.category,
        description: newDeviation.description,
        boq_item: newDeviation.boq_item,
        original_qty: originalQty,
        revised_qty: revisedQty,
        unit: 'Nos',
        original_rate: originalRate,
        revised_rate: revisedRate,
        original_amount: originalAmount,
        revised_amount: revisedAmount,
        variance_amount: varianceAmount,
        variance_pct: variancePct,
        raised_by: JSON.parse(localStorage.getItem('user') || '{}')?.name || 'User',
        raised_date: new Date().toISOString().slice(0, 10),
        status: row.status || 'Submitted',
        approved_by: null,
        approved_date: null,
        linked_correspondence: null,
        remarks: newDeviation.remarks,
        attachments: newDeviation.attachments || [],
      };

      setDeviations((prev) =>
        editingId ? prev.map((d) => (d.id === editingId ? mapped : d)) : [mapped, ...prev],
      );
      setShowModal(false);
      setEditingId(null);
      setNewDeviation({
        project_id: '',
        category: 'Deviation',
        title: '',
        description: '',
        boq_item: '',
        original_qty: '',
        revised_qty: '',
        original_rate: '',
        revised_rate: '',
        remarks: '',
        attachments: [],
      });
      notifySuccess(editingId ? 'Deviation updated successfully.' : 'Deviation created successfully.');
    } catch (error) {
      console.error('Save deviation failed via API:', error);
      notifyError('Unable to save deviation. Please retry.');
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewDeviation((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  return (
    <div>
      <div className="section-header">
        <h2>Deviations & Extra Items <span className="count-badge">{totalCount}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Deviations & Extra Items - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              setEditingId(null);
              setNewDeviation({
                project_id: '',
                category: 'Deviation',
                title: '',
                description: '',
                boq_item: '',
                original_qty: '',
                revised_qty: '',
                original_rate: '',
                revised_rate: '',
                remarks: '',
                attachments: [],
              });
              setShowModal(true);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New Deviation
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Total Variance</p>
            <p className="stat-value" style={{ fontSize: 22 }}>{formatCurrency(totalVariance)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#EDE9FE' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Pending Approval</p>
            <p className="stat-value">{filteredItems.filter(i => i.status !== 'Approved' && i.status !== 'Rejected').length}</p>
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
            value={projectFilter}
            onChange={(v) => { setProjectFilter(v); setPage(1); }}
            projects={projects}
          />
        <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
          <option value="all">All Categories</option>
          <option value="Deviation">Deviation</option>
          <option value="Extra Item">Extra Item</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Under Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setCategoryFilter('all');
            setStatusFilter('all');
            setPage(1);
          }}
        />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              {showProjectColumn && <th>Project</th>}
              <th>Deviation No</th>
              <th>Title</th>
              <th>Category</th>
              <th>BOQ Item</th>
              <th className="amount-cell">Original Amt</th>
              <th className="amount-cell">Revised Amt</th>
              <th className="amount-cell">Variance</th>
              <th className="amount-cell">Variance %</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <tr><td colSpan={showProjectColumn ? 10 : 9} style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No deviations found.</td></tr>
            ) : filteredItems.map((item) => (
                <tr key={item.id} className="clickable" onClick={() => setSelectedItem(item)}>
                {showProjectColumn && (
                  <td>{projects.find((p) => p.id === item.project_id)?.name || '-'}</td>
                )}
                <td style={{ fontWeight: 500, color: '#7C3AED' }}>{item.deviation_no}</td>
                <td style={{ fontWeight: 500, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</td>
                <td><StatusBadge tone={item.category === 'Deviation' ? 'high' : 'medium'}>{item.category}</StatusBadge></td>
                <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.boq_item}</td>
                <td className="amount-cell">{formatCurrency(item.original_amount)}</td>
                <td className="amount-cell" style={{ fontWeight: 500 }}>{formatCurrency(item.revised_amount)}</td>
                <td className="amount-cell" style={{ fontWeight: 500, color: '#b91c1c' }}>+{formatCurrency(item.variance_amount)}</td>
                <td className="amount-cell" style={{ color: item.variance_pct > 50 ? '#b91c1c' : item.variance_pct > 25 ? '#b45309' : '#374151' }}>{item.variance_pct.toFixed(1)}%</td>
                <td><StatusBadge tone={getStatusClass(item.status)}>{item.status}</StatusBadge></td>
                <td>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingId(item.id);
                      setNewDeviation({
                        project_id: String(item.project_id),
                        category: item.category,
                        title: item.title,
                        description: item.description || '',
                        boq_item: item.boq_item || '',
                        original_qty: item.original_qty ?? '',
                        revised_qty: item.revised_qty ?? '',
                        original_rate: item.original_rate ?? '',
                        revised_rate: item.revised_rate ?? '',
                        remarks: item.remarks || '',
                        attachments: item.attachments || [],
                      });
                      setShowModal(true);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    style={{ marginLeft: 8, color: '#b91c1c', borderColor: '#fecaca' }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!(await confirmDialog('Delete this deviation?', 'Delete Deviation'))) return;
                      try {
                        await deviationsAPI.delete(item.id);
                        setDeviations((prev) => prev.filter((d) => d.id !== item.id));
                        notifySuccess('Deviation deleted.');
                      } catch (error) {
                        console.error('Delete deviation failed:', error);
                        notifyError('Unable to delete deviation. Please retry.');
                      }
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={page}
        totalPages={totalPages}
        onPrevious={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />

      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{selectedItem.deviation_no} - Detail</h2>
              <button className="btn-close" onClick={() => setSelectedItem(null)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: '0 0 8px 0' }}>{selectedItem.title}</h3>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <StatusBadge tone={selectedItem.category === 'Deviation' ? 'high' : 'medium'}>{selectedItem.category}</StatusBadge>
                <StatusBadge tone={getStatusClass(selectedItem.status)}>{selectedItem.status}</StatusBadge>
              </div>
              <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6, marginBottom: 20 }}>{selectedItem.description}</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>BOQ Item</p><p style={{ fontSize: 13, fontWeight: 500 }}>{selectedItem.boq_item}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Unit</p><p style={{ fontSize: 13 }}>{selectedItem.unit}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Original Qty / Rate</p><p style={{ fontSize: 13 }}>{selectedItem.original_qty} x {formatCurrency(selectedItem.original_rate)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Revised Qty / Rate</p><p style={{ fontSize: 13, fontWeight: 500 }}>{selectedItem.revised_qty} x {formatCurrency(selectedItem.revised_rate)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Original Amount</p><p style={{ fontSize: 14 }}>{formatCurrency(selectedItem.original_amount)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Revised Amount</p><p style={{ fontSize: 14, fontWeight: 600, color: '#7C3AED' }}>{formatCurrency(selectedItem.revised_amount)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Variance</p><p style={{ fontSize: 14, fontWeight: 600, color: '#b91c1c' }}>+{formatCurrency(selectedItem.variance_amount)} ({selectedItem.variance_pct}%)</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Raised By</p><p style={{ fontSize: 13 }}>{selectedItem.raised_by} on {formatDateIN(selectedItem.raised_date)}</p></div>
                {selectedItem.approved_by && <div><p style={{ fontSize: 12, color: '#6b7280' }}>Approved By</p><p style={{ fontSize: 13 }}>{selectedItem.approved_by} on {formatDateIN(selectedItem.approved_date)}</p></div>}
              </div>
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedItem.linked_correspondence} />
              {(selectedItem.attachments || []).length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Attachments</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {selectedItem.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `Deviation attachment: ${file?.name}`))}>View</button>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `Deviation attachment: ${file?.name}`)}>Download</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div><p style={{ fontSize: 12, color: '#6b7280' }}>Remarks</p><p style={{ fontSize: 13, color: '#374151' }}>{selectedItem.remarks}</p></div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New Deviation / Extra Item</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}><svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Project <span className="req">*</span></label><select value={newDeviation.project_id} onChange={(e) => setNewDeviation({ ...newDeviation, project_id: e.target.value })}><option value="">Select</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div className="form-group"><label>Category</label><select value={newDeviation.category} onChange={(e) => setNewDeviation({ ...newDeviation, category: e.target.value })}><option value="Deviation">Deviation</option><option value="Extra Item">Extra Item</option></select></div>
              </div>
              <div className="form-group"><label>Title <span className="req">*</span></label><input type="text" value={newDeviation.title} onChange={(e) => setNewDeviation({ ...newDeviation, title: e.target.value })} placeholder="Brief title of deviation" /></div>
              <div className="form-group"><label>Description</label><textarea value={newDeviation.description} onChange={(e) => setNewDeviation({ ...newDeviation, description: e.target.value })} placeholder="Detailed description..." rows={3} /></div>
              <div className="form-group"><label>BOQ Item</label><input type="text" value={newDeviation.boq_item} onChange={(e) => setNewDeviation({ ...newDeviation, boq_item: e.target.value })} placeholder="Related BOQ item" /></div>
              <div className="form-row">
                <div className="form-group"><label>Original Qty</label><input type="number" value={newDeviation.original_qty} onChange={(e) => setNewDeviation({ ...newDeviation, original_qty: e.target.value })} placeholder="0" /></div>
                <div className="form-group"><label>Revised Qty</label><input type="number" value={newDeviation.revised_qty} onChange={(e) => setNewDeviation({ ...newDeviation, revised_qty: e.target.value })} placeholder="0" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Original Rate (₹)</label><input type="number" value={newDeviation.original_rate} onChange={(e) => setNewDeviation({ ...newDeviation, original_rate: e.target.value })} placeholder="0" /></div>
                <div className="form-group"><label>Revised Rate (₹)</label><input type="number" value={newDeviation.revised_rate} onChange={(e) => setNewDeviation({ ...newDeviation, revised_rate: e.target.value })} placeholder="0" /></div>
              </div>
              <div className="form-group"><label>Remarks</label><textarea value={newDeviation.remarks} onChange={(e) => setNewDeviation({ ...newDeviation, remarks: e.target.value })} placeholder="Justification..." rows={2} /></div>
              <div className="form-group">
                <label>Attachments</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newDeviation.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {newDeviation.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setNewDeviation((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleSaveDeviation} disabled={attachmentUploading}>
                  {editingId ? 'Save Changes' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Deviations;
